import { useEffect } from "react";
import type { Movie } from "@/types/movie";

interface PlayModalProps {
  open: boolean;
  movie: Movie | null;
  onClose: () => void;
}

export default function PlayModal({ open, movie, onClose }: PlayModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !movie) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="播放演示"
    >
      <button
        type="button"
        aria-label="关闭"
        onClick={onClose}
        className="absolute inset-0 bg-background-50/90 backdrop-blur-sm cursor-pointer"
      ></button>

      <div className="relative w-full max-w-3xl rounded-lg overflow-hidden bg-background-100 border border-background-200 animate-fade-up">
        <div className="flex items-center justify-between px-4 md:px-6 h-14 border-b border-background-200">
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 truncate pr-4">
            正在播放：{movie.title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-950 hover:bg-background-200 transition-colors cursor-pointer"
            aria-label="关闭播放窗口"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="relative w-full aspect-video bg-background-50 flex flex-col items-center justify-center text-center px-6">
          <span className="w-16 h-16 rounded-full bg-primary-500/90 text-foreground-950 flex items-center justify-center animate-soft-pulse">
            <i className="ri-play-fill text-3xl"></i>
          </span>
          <p className="mt-5 font-heading text-lg md:text-xl font-bold text-foreground-900">
            播放功能为演示状态
          </p>
          <p className="mt-2 max-w-lg text-xs md:text-sm leading-6 text-foreground-500">
            因暂无真实播放接口，当前播放区域仅为前端效果演示，无法真正播放视频内容。后续可接入真实播放服务后启用。
          </p>
          <div className="mt-6 flex items-center gap-2 text-xs text-foreground-500">
            <span className="px-3 py-1 rounded-full bg-secondary-900/60 text-secondary-200">
              演示模式
            </span>
            <span className="px-3 py-1 rounded-full bg-background-200 text-foreground-600">
              {movie.duration}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 border-t border-background-200 flex-wrap">
          <p className="text-xs text-foreground-500">
            <i className="ri-information-line mr-1"></i>
            本站为影视资源展示演示站，所有播放行为均为模拟。
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-label whitespace-nowrap transition-colors cursor-pointer"
          >
            我知道了
          </button>
        </div>
      </div>
    </div>
  );
}